import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'microsoft-outlook-trigger',
  version: '1.0.0',
  properties: [
    // === Authentication ===
    {
      key: 'credentialId',
      label: 'Microsoft Outlook Credential',
      type: 'credential',
      required: true,
      description: 'Select the Microsoft Outlook OAuth2 credential to use',
      typeOptions: {
        credentialType: 'microsoft_outlook_oauth',
      },
    },

    // === Polling Configuration ===
    {
      key: 'pollMode',
      label: 'Poll Frequency',
      type: 'options',
      default: 'everyXMinutes',
      description: 'How often to check for new emails',
      options: [
        {
          name: 'Every X Minutes',
          value: 'everyXMinutes',
        },
        {
          name: 'Every Hour',
          value: 'everyHour',
        },
        {
          name: 'Every Day',
          value: 'everyDay',
        },
        {
          name: 'Custom (Cron)',
          value: 'custom',
        },
      ],
    },
    {
      key: 'minuteInterval',
      label: 'Poll Every',
      type: 'options',
      default: 5,
      description: 'Poll for new emails every X minutes',
      options: [
        { name: '1 minute', value: 1 },
        { name: '5 minutes', value: 5 },
        { name: '10 minutes', value: 10 },
        { name: '15 minutes', value: 15 },
        { name: '30 minutes', value: 30 },
      ],
      displayOptions: {
        show: {
          pollMode: ['everyXMinutes'],
        },
      },
    },
    {
      key: 'minute',
      label: 'Minute',
      type: 'number',
      default: 0,
      description: 'Minute of the hour (0-59)',
      displayOptions: {
        show: {
          pollMode: ['everyHour', 'everyDay'],
        },
      },
      typeOptions: {
        minValue: 0,
        maxValue: 59,
      },
    },
    {
      key: 'hour',
      label: 'Hour',
      type: 'number',
      default: 9,
      description: 'Hour of the day (0-23)',
      displayOptions: {
        show: {
          pollMode: ['everyDay'],
        },
      },
      typeOptions: {
        minValue: 0,
        maxValue: 23,
      },
    },
    {
      key: 'cronExpression',
      label: 'Cron Expression',
      type: 'string',
      default: '*/5 * * * *',
      placeholder: '*/5 * * * *',
      description: 'Custom cron expression (minute hour day month dayOfWeek)',
      displayOptions: {
        show: {
          pollMode: ['custom'],
        },
      },
    },
    {
      key: 'timezone',
      label: 'Timezone',
      type: 'options',
      default: 'UTC',
      description: 'Timezone for the polling schedule',
      options: [
        { value: 'UTC', name: 'UTC' },
        { value: 'America/New_York', name: 'Eastern Time (US)' },
        { value: 'America/Chicago', name: 'Central Time (US)' },
        { value: 'America/Denver', name: 'Mountain Time (US)' },
        { value: 'America/Los_Angeles', name: 'Pacific Time (US)' },
        { value: 'America/Toronto', name: 'Toronto' },
        { value: 'America/Vancouver', name: 'Vancouver' },
        { value: 'Europe/London', name: 'London' },
        { value: 'Europe/Paris', name: 'Paris' },
        { value: 'Europe/Berlin', name: 'Berlin' },
        { value: 'Asia/Tokyo', name: 'Tokyo' },
        { value: 'Asia/Shanghai', name: 'Shanghai' },
        { value: 'Asia/Singapore', name: 'Singapore' },
        { value: 'Australia/Sydney', name: 'Sydney' },
      ],
    },

    // === Email Filters ===
    {
      key: 'filterBySender',
      label: 'Filter by Sender',
      type: 'string',
      default: '',
      placeholder: 'sender@example.com',
      description: 'Only trigger for emails from this sender (leave empty for all)',
    },
    {
      key: 'filterByReadStatus',
      label: 'Read Status',
      type: 'options',
      default: 'all',
      description: 'Filter by read/unread status',
      options: [
        { name: 'All Messages', value: 'all' },
        { name: 'Unread Only', value: 'unread' },
        { name: 'Read Only', value: 'read' },
      ],
    },
    {
      key: 'filterHasAttachments',
      label: 'Has Attachments',
      type: 'options',
      default: 'all',
      description: 'Filter by attachment status',
      options: [
        { name: 'All Messages', value: 'all' },
        { name: 'With Attachments', value: 'yes' },
        { name: 'Without Attachments', value: 'no' },
      ],
    },
    {
      key: 'foldersToInclude',
      label: 'Folders to Monitor',
      type: 'string',
      default: 'inbox',
      placeholder: 'inbox, archive',
      description: 'Comma-separated list of folders to monitor (inbox, drafts, sent, deleted, archive, junk)',
    },
    {
      key: 'customODataFilter',
      label: 'Custom OData Filter',
      type: 'string',
      default: '',
      placeholder: "subject eq 'Important'",
      description: 'Advanced OData filter expression (optional)',
      displayOptions: {
        show: {
          advancedOptions: [true],
        },
      },
    },

    // === Output Configuration ===
    {
      key: 'outputMode',
      label: 'Output Format',
      type: 'options',
      default: 'simple',
      description: 'How to format the message data',
      options: [
        {
          name: 'Simple (Key fields only)',
          value: 'simple',
        },
        {
          name: 'Raw (Full API response)',
          value: 'raw',
        },
        {
          name: 'Select Fields',
          value: 'selectFields',
        },
      ],
    },
    {
      key: 'selectedFields',
      label: 'Fields to Include',
      type: 'multiOptions',
      default: ['id', 'subject', 'from', 'to', 'receivedDateTime', 'bodyPreview', 'isRead', 'hasAttachments'],
      description: 'Select which fields to include in the output',
      options: [
        { name: 'ID', value: 'id' },
        { name: 'Subject', value: 'subject' },
        { name: 'From', value: 'from' },
        { name: 'To Recipients', value: 'toRecipients' },
        { name: 'CC Recipients', value: 'ccRecipients' },
        { name: 'BCC Recipients', value: 'bccRecipients' },
        { name: 'Body Preview', value: 'bodyPreview' },
        { name: 'Body Content', value: 'body' },
        { name: 'Received Date/Time', value: 'receivedDateTime' },
        { name: 'Sent Date/Time', value: 'sentDateTime' },
        { name: 'Is Read', value: 'isRead' },
        { name: 'Has Attachments', value: 'hasAttachments' },
        { name: 'Importance', value: 'importance' },
        { name: 'Categories', value: 'categories' },
        { name: 'Conversation ID', value: 'conversationId' },
        { name: 'Internet Message ID', value: 'internetMessageId' },
      ],
      displayOptions: {
        show: {
          outputMode: ['selectFields'],
        },
      },
    },

    // === Attachments ===
    {
      key: 'downloadAttachments',
      label: 'Download Attachments',
      type: 'boolean',
      default: false,
      description: 'Download email attachments as Base64 encoded content',
    },
    {
      key: 'maxAttachmentsPerMessage',
      label: 'Max Attachments per Message',
      type: 'number',
      default: 10,
      description: 'Maximum number of attachments to download per message',
      displayOptions: {
        show: {
          downloadAttachments: [true],
        },
      },
      typeOptions: {
        minValue: 1,
        maxValue: 50,
      },
    },
    {
      key: 'decodeTextContent',
      label: 'Decode Text Attachments',
      type: 'boolean',
      default: false,
      description: 'Automatically decode text attachments (CSV, JSON, TXT, XML) to readable text. When enabled, returns "content" as text in addition to contentBase64.',
      displayOptions: {
        show: {
          downloadAttachments: [true],
        },
      },
    },
    {
      key: 'parseJsonContent',
      label: 'Parse JSON Attachments',
      type: 'boolean',
      default: false,
      description: 'Automatically parse JSON attachments into an object/array. When enabled, returns "json" as the parsed data. This is faster than using a Code node for large JSON files.',
      displayOptions: {
        show: {
          downloadAttachments: [true],
          decodeTextContent: [true],
        },
      },
    },

    // === Advanced Options ===
    {
      key: 'advancedOptions',
      label: 'Show Advanced Options',
      type: 'boolean',
      default: false,
      description: 'Show additional configuration options',
    },
    {
      key: 'initialLookbackHours',
      label: 'Initial Lookback Hours',
      type: 'number',
      default: 1,
      description: 'How many hours to look back on first poll (before any state is saved)',
      displayOptions: {
        show: {
          advancedOptions: [true],
        },
      },
      typeOptions: {
        minValue: 1,
        maxValue: 168, // 1 week
      },
    },
    {
      key: 'markAsRead',
      label: 'Mark as Read',
      type: 'boolean',
      default: false,
      description: 'Mark retrieved messages as read after processing',
      displayOptions: {
        show: {
          advancedOptions: [true],
        },
      },
    },
    {
      key: 'maxMessagesPerPoll',
      label: 'Max Messages per Poll',
      type: 'number',
      default: 100,
      description: 'Maximum number of messages to retrieve per poll',
      displayOptions: {
        show: {
          advancedOptions: [true],
        },
      },
      typeOptions: {
        minValue: 1,
        maxValue: 1000,
      },
    },
  ],
};

export default schema;
